/**
 * marginBridge — the Sage ledger and the invoice fact, month against month, at ANY scope.
 *
 * WHY IT EXISTS. The bottom panel used to show the Group→Entity split at EVERY scope — a
 * decomposition of a number an entity page is no longer reporting, with the site you are
 * standing on as one of its columns. The question this asks instead is **does what we measured
 * agree with what Sage posted, and where does it not?** The two sides share a clock — the
 * invoice date — so month by month is the grain to ask it at.
 *
 * ⭐ IT RUNS AT GROUP TOO (24 Aug 2026). Group answered only "what makes the number up"
 * (MarginComposition), which states the whole period once, so the parent carried a coarser
 * answer than either child. Group is a valid subject here because both sides of the test sum
 * the same two entities: Sage Group = Kirkstall + Oracle on served cells, and the invoice fact
 * pools the same two ERPs. Group 2026 Jan–Jul reads £4,054,848 against £4,115,592, +1.5%.
 *
 * ⚠ ONLY THE REVENUE LEG IS A PASS/FAIL TEST, and only that leg is graded.
 *
 * - REVENUE is like-for-like: both sides are every billed line in the month. Kirkstall agrees
 *   to +0.11% and Oracle to −0.56% over 2026, so a month that misses by more is worth a look.
 * - COST IS NOT, at month grain, AT EITHER SITE. Sage posts cost by PERIOD, not against the
 *   jobs that earned the revenue: Kirkstall's own Sage gross margin swings 24.2 points across
 *   2026 Jan–Jul and still ties to job-actual within 1.3% pooled. A monthly cost delta is Sage's
 *   periodisation, not our error, and grading it would put a red chip on a correct figure
 *   seven times a year. The TOTAL row is where the cost comparison means something.
 * - AND OUR COST PRICES ONLY PART OF THE REVENUE. `costed` is the denominator our GM% is taken
 *   on; at Oracle it is 66.2% of billed value. Comparing a 66%-covered cost against a
 *   100%-covered COGS is not a variance, and the column is there so nobody reads it as one.
 *
 * Pure functions, no fetch (CD-7).
 */

import { periodHeading } from '../components/kit/cellFormat'
import { pick, WINDOW_PERIOD } from './margin'
import type { Cell, DrillRow, Site } from '../types/metrics'
import type { MarginTimeline } from '../types/margin'

/** How far a month's revenue may miss Sage before the surface says so. Both sides count the
 * same billed lines, so this is tight on purpose — it is a real test, not a tolerance band. */
const REVENUE_GOOD = 1
const REVENUE_WATCH = 5

export type Grade = 'good' | 'watch' | 'bad' | 'none'

export interface BridgeRow {
  /** '2026-01', or WINDOW_PERIOD for the pooled row. */
  period: string
  label: string
  isTotal: boolean
  sageRevenue: number | null
  sageCogs: number | null
  sageGmPct: number | null
  ourRevenue: number | null
  /** The part of `ourRevenue` that carries a cost — the denominator `ourGmPct` is taken on. */
  ourCosted: number | null
  ourCost: number | null
  ourGmPct: number | null
  /** (ours − Sage) ÷ Sage, as a percentage. The graded one. */
  revenueDeltaPct: number | null
  costDeltaPct: number | null
  gmDeltaPt: number | null
  coveragePct: number | null
  revenueGrade: Grade
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const value = (cell: Cell | null): number | null =>
  cell && cell.value !== null ? cell.value : null

/** (ours − theirs) ÷ theirs, as a percentage. Null when there is nothing to divide by — a
 * delta against zero is not a large variance, it is an undefined one. */
function deltaPct(ours: number | null, theirs: number | null): number | null {
  if (ours === null || theirs === null || theirs === 0) return null
  return (100 * (ours - theirs)) / theirs
}

function gradeRevenue(delta: number | null): Grade {
  if (delta === null) return 'none'
  const size = Math.abs(delta)
  return size < REVENUE_GOOD ? 'good' : size < REVENUE_WATCH ? 'watch' : 'bad'
}

function ratioPct(top: number | null, bottom: number | null): number | null {
  if (top === null || bottom === null || bottom === 0) return null
  return (100 * top) / bottom
}

/** One month, both sides. `ours` is null for a month Sage posted and we billed nothing into —
 * the row still draws, because a month present on one side and absent on the other is the most
 * interesting row on the page and must never be dropped for being half empty. */
function row(
  period: string,
  label: string,
  isTotal: boolean,
  sage: { revenue: number | null; cogs: number | null },
  ours: { revenue: number | null; costed: number | null; cost: number | null; gmPct: number | null }
): BridgeRow {
  const sageGmPct = sage.revenue !== null && sage.cogs !== null
    ? ratioPct(sage.revenue - sage.cogs, sage.revenue)
    : null
  const revenueDeltaPct = deltaPct(ours.revenue, sage.revenue)
  return {
    period,
    label,
    isTotal,
    sageRevenue: sage.revenue,
    sageCogs: sage.cogs,
    sageGmPct,
    ourRevenue: ours.revenue,
    ourCosted: ours.costed,
    ourCost: ours.cost,
    ourGmPct: ours.gmPct,
    revenueDeltaPct,
    costDeltaPct: deltaPct(ours.cost, sage.cogs),
    gmDeltaPt: ours.gmPct !== null && sageGmPct !== null ? ours.gmPct - sageGmPct : null,
    coveragePct: ratioPct(ours.costed, ours.revenue),
    revenueGrade: gradeRevenue(revenueDeltaPct),
  }
}

/** The invoice fact's months, keyed by the served month id. */
function ourMonths(rows: DrillRow[]): Map<string, DrillRow> {
  const byMonth = new Map<string, DrillRow>()
  for (const r of rows) {
    const month = r['Invoice month']
    if (typeof month === 'string') byMonth.set(month, r)
  }
  return byMonth
}

export interface Bridge {
  rows: BridgeRow[]
  /** Months Sage posted that the invoice fact has no row for, and the reverse. Named rather
   * than quietly rendered as an em-dash, because a one-sided month is a finding. */
  missing: { sageOnly: string[]; oursOnly: string[] }
}

/**
 * Build the bridge for one site over the CLOSED months both sides agree to report.
 *
 * `months` comes from windowSummary — the closed Sage months — and the invoice fact serves
 * closed months too, by the same rule. Where the two sets differ the row is still drawn and
 * the difference is reported, because that IS the answer to "when do things not match".
 */
export function bridge(
  lookup: Map<string, Cell>,
  months: string[],
  site: Site,
  timeline: MarginTimeline | null,
  windowLabel: string
): Bridge {
  const ours = ourMonths(timeline?.rows ?? [])
  const periods = [...new Set([...months, ...ours.keys()])].sort()
  const sageAt = (period: string) => ({
    revenue: value(pick(lookup, 'revenue', site, period)),
    cogs: value(pick(lookup, 'cogs', site, period)),
  })
  const total = timeline?.total.cost ?? null
  const rows = periods.map((period) => {
    const mine = ours.get(period)
    return row(period, periodHeading(period), false, sageAt(period), {
      revenue: num(mine?.LineValue),
      costed: num(mine?.Costed),
      cost: num(mine?.Cost),
      gmPct: num(mine?.['GM%']),
    })
  })
  rows.push(row(WINDOW_PERIOD, windowLabel, true, sageAt(WINDOW_PERIOD), {
    revenue: timeline?.total.value ?? null,
    costed: total?.costed_value ?? null,
    cost: total?.cost ?? null,
    gmPct: total?.gm_pct ?? null,
  }))
  return {
    rows,
    missing: {
      sageOnly: months.filter((period) => !ours.has(period)),
      oursOnly: [...ours.keys()].filter((period) => !months.includes(period)),
    },
  }
}
